import React from 'react';
import { useOAuthCallback } from '../hooks/useOAuthCallback';
import { authApi } from '../api/auth';
import WelcomeModal from '../components/WelcomeModal';

/**
 * GitHub OAuth Callback Handler
 *
 * ARCHITECTURE NOTE:
 * GitHub OAuth uses a redirect-based authorization code flow (consistent with Google/Apple).
 * This component handles the redirect callback from GitHub after user authentication.
 *
 * FLOW:
 * 1. User clicks "Sign in with GitHub" → SocialLogin initiates OAuth flow
 * 2. User authenticates on GitHub → GitHub redirects back with authorization code
 * 3. This component receives code & state → Exchanges code for tokens via backend
 * 4. Backend validates CSRF state and returns complete AuthResponse (access_token, refresh_token, user)
 * 5. Component calls handleOAuthCallback() to store tokens and update AuthContext
 * 6. Navigate to dashboard → User is authenticated
 *
 * See also: SocialLogin.tsx (initiation), AuthContext.tsx (handleOAuthCallback), useOAuthCallback.ts (shared logic)
 */
const GitHubCallback: React.FC = () => {
  const { showWelcome, userInfo, handleWelcomeClose } = useOAuthCallback(
    'github',
    ({ code, state }) => authApi.githubCallback({ code, state })
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Completing GitHub Sign In...</h2>
          <p className="text-gray-600 mt-2">Please wait while we authenticate your account.</p>
        </div>
      </div>
      {showWelcome && userInfo && (
        <WelcomeModal
          isOpen={showWelcome}
          displayName={userInfo.displayName}
          email={userInfo.email}
          provider="github"
          onClose={handleWelcomeClose}
        />
      )}
    </>
  );
};

export default GitHubCallback;
